import Check from '@mui/icons-material/Check';
import EditNoteOutlined from '@mui/icons-material/EditNoteOutlined';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import {
  AiToolCallState,
  TreadAgentSingleRecordWriteType,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useState } from 'react';

import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import type { TreadAgentMessage } from '~hooks/useTreadAgent/models';
import ConfirmationCardShell, {
  ApplyingIndicator,
} from '~tread-agent/ConfirmationCardShell';
import { COMPACT_ACTION_BUTTON_SX } from '~tread-agent/confirmationCardStyles';
import TerminalStatusIcon from '~tread-agent/TerminalStatusIcon';
import ToolCallPreviewDetails from '~tread-agent/ToolCallPreviewDetails';
import { confirmationTitle } from '~tread-agent/treadAgentUtils';
import { BulkConfirmDecision, TreadAgentToolCallState } from '~tread-agent/types';

const COLLAPSED_ROW_LIMIT = 6;

type ToolCall = TreadAgentMessage['toolCalls'][number];

interface BulkConfirmationCardProps {
  isBusy: boolean;
  onSubmit: (decision: BulkConfirmDecision) => void;
  toolCalls: ToolCall[];
}

function RowStatusIcon({ toolCall }: { toolCall: ToolCall }) {
  if (toolCall.state === AiToolCallState.CONFIRMING) {
    return <CircularProgress size={14} />;
  }

  return <TerminalStatusIcon state={toolCall.state} />;
}

export default function BulkConfirmationCard({
  isBusy,
  onSubmit,
  toolCalls,
}: BulkConfirmationCardProps) {
  const theme = useTheme();
  const [uncheckedIds, setUncheckedIds] = useState<string[]>([]);
  const [isExpanded, setIsExpanded] = useState(false);
  const [detailsOpenIds, setDetailsOpenIds] = useState<string[]>([]);

  const awaitingToolCalls = toolCalls.filter(
    (toolCall) => toolCall.state === AiToolCallState.CONFIRMATION_REQUIRED,
  );
  const isAwaitingAnswer = awaitingToolCalls.length > 0;
  // isBusy outlives the request, so it only counts while some row is still waiting for its answer
  const isApplying =
    (isBusy && isAwaitingAnswer) ||
    toolCalls.some((toolCall) => toolCall.state === AiToolCallState.CONFIRMING);
  const confirmedIds = awaitingToolCalls
    .filter((toolCall) => !uncheckedIds.includes(toolCall.id))
    .map((toolCall) => toolCall.id);
  const canceledIds = awaitingToolCalls
    .filter((toolCall) => uncheckedIds.includes(toolCall.id))
    .map((toolCall) => toolCall.id);
  // Confirm N covers every awaiting row, so the list only collapses once nothing is awaiting.
  const isCollapsible = !isAwaitingAnswer && toolCalls.length > COLLAPSED_ROW_LIMIT;
  const visibleToolCalls =
    isCollapsible && !isExpanded ? toolCalls.slice(0, COLLAPSED_ROW_LIMIT) : toolCalls;
  const hiddenRowCount = toolCalls.length - visibleToolCalls.length;
  const countByState = (state: TreadAgentToolCallState) =>
    toolCalls.filter((toolCall) => toolCall.state === state).length;
  const appliedCount = countByState(AiToolCallState.APPLIED);
  const failedCount = countByState(AiToolCallState.FAILED);
  const canceledCount = countByState(AiToolCallState.CANCELED);
  const expiredCount = countByState(AiToolCallState.EXPIRED);
  const outcomeSummary = [
    appliedCount > 0 &&
      t('tread_agent.confirmation.applied_count', { count: appliedCount }),
    failedCount > 0 && t('tread_agent.confirmation.failed_count', { count: failedCount }),
    canceledCount > 0 &&
      t('tread_agent.confirmation.canceled_count', { count: canceledCount }),
    expiredCount > 0 &&
      t('tread_agent.confirmation.expired_count', { count: expiredCount }),
  ]
    .filter(Boolean)
    .join(', ');
  const isShowingProgress = isAwaitingAnswer && isApplying;
  const isShowingActions = isAwaitingAnswer && !isApplying;
  const isShowingOutcome = !isAwaitingAnswer && !isApplying && outcomeSummary !== '';

  const toggleDetails = (toolCallId: string) =>
    setDetailsOpenIds((current) =>
      current.includes(toolCallId)
        ? current.filter((id) => id !== toolCallId)
        : [...current, toolCallId],
    );

  const toggleChecked = (toolCallId: string) =>
    setUncheckedIds((current) =>
      current.includes(toolCallId)
        ? current.filter((id) => id !== toolCallId)
        : [...current, toolCallId],
    );

  return (
    <ConfirmationCardShell dataTestId="tread-agent-bulk-confirmation-card">
      <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.75 }}>
        <EditNoteOutlined sx={{ color: theme.brandV2.colors.treadGray3, fontSize: 16 }} />
        <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
          {t('tread_agent.confirmation.group_title', { count: toolCalls.length })}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
        {visibleToolCalls.map((toolCall) => {
          const isAwaiting = toolCall.state === AiToolCallState.CONFIRMATION_REQUIRED;
          const error = toolCall.output?.error;
          const failureReason =
            toolCall.state === AiToolCallState.FAILED
              ? typeof error === 'string' && error !== ''
                ? error
                : t('tread_agent.confirmation.failed')
              : '';
          const recordLabel =
            toolCall.preview?.kind === TreadAgentSingleRecordWriteType.SINGLE_RECORD_WRITE
              ? toolCall.preview.target.label
              : null;
          const hasDetails = toolCall.preview !== null;
          const isDetailsOpen = detailsOpenIds.includes(toolCall.id);

          return (
            <Box key={toolCall.id} sx={{ display: 'flex', flexDirection: 'column' }}>
              <Box
                sx={{ alignItems: 'center', display: 'flex', gap: 0.75, minHeight: 28 }}
              >
                {isAwaiting ? (
                  <Checkbox
                    checked={!uncheckedIds.includes(toolCall.id)}
                    disabled={isApplying}
                    onChange={() => toggleChecked(toolCall.id)}
                    size="small"
                    sx={{ p: 0.25 }}
                  />
                ) : (
                  <Box sx={{ display: 'flex', justifyContent: 'center', width: 24 }}>
                    <RowStatusIcon toolCall={toolCall} />
                  </Box>
                )}

                <Typography sx={{ flex: 1, fontSize: '12px', minWidth: 0 }}>
                  {confirmationTitle(toolCall.preview, toolCall.name)}
                  {recordLabel !== null && (
                    <Box
                      component="span"
                      sx={{
                        color: theme.brandV2.colors.treadGray3,
                        ml: 0.5,
                        '&::before': { content: '"· "' },
                      }}
                    >
                      {recordLabel}
                    </Box>
                  )}
                  {failureReason && (
                    <Box
                      component="span"
                      sx={{ color: theme.brandV2.colors.treadRed, ml: 0.75 }}
                    >
                      {failureReason}
                    </Box>
                  )}
                </Typography>

                {hasDetails && (
                  <IconButton
                    aria-expanded={isDetailsOpen}
                    aria-label={t('tread_agent.confirmation.show_details')}
                    onClick={() => toggleDetails(toolCall.id)}
                    size="small"
                    sx={{ flexShrink: 0, p: 0.25 }}
                  >
                    <ExpandMore
                      sx={{
                        color: theme.brandV2.colors.treadGray3,
                        fontSize: 18,
                        transform: isDetailsOpen ? 'rotate(180deg)' : 'none',
                        transition: 'transform 0.15s ease',
                      }}
                    />
                  </IconButton>
                )}
              </Box>

              {hasDetails && isDetailsOpen && (
                <Box sx={{ pb: 0.5, pl: 4 }}>
                  <ToolCallPreviewDetails preview={toolCall.preview} />
                </Box>
              )}
            </Box>
          );
        })}

        {isCollapsible && (
          <SmallButton
            color="secondary"
            onClick={() => setIsExpanded((current) => !current)}
            sx={{ alignSelf: 'flex-start', ...COMPACT_ACTION_BUTTON_SX }}
            variant="text"
          >
            {isExpanded
              ? t('tread_agent.confirmation.show_fewer')
              : t('tread_agent.confirmation.show_more', { count: hiddenRowCount })}
          </SmallButton>
        )}
      </Box>

      {isShowingProgress && <ApplyingIndicator />}

      {isShowingActions && (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <SmallButton
            color="secondary"
            onClick={() =>
              onSubmit({
                canceledIds: awaitingToolCalls.map((toolCall) => toolCall.id),
                confirmedIds: [],
              })
            }
            sx={COMPACT_ACTION_BUTTON_SX}
            variant="outlined"
          >
            {t('tread_agent.confirmation.cancel_all')}
          </SmallButton>

          <SmallLoadingButton
            color="brandV2Green"
            disabled={confirmedIds.length === 0}
            onClick={() => onSubmit({ canceledIds, confirmedIds })}
            startIcon={<Check sx={{ fontSize: 14 }} />}
            sx={COMPACT_ACTION_BUTTON_SX}
            variant="contained"
          >
            {t('tread_agent.confirmation.confirm_count', {
              count: confirmedIds.length,
            })}
          </SmallLoadingButton>
        </Box>
      )}

      {isShowingOutcome && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography
            sx={{
              color: theme.brandV2.colors.treadGray3,
              fontSize: '12px',
              fontWeight: 500,
            }}
          >
            {outcomeSummary}
          </Typography>
        </Box>
      )}
    </ConfirmationCardShell>
  );
}
